import { useState } from "react";
import type { BindingTargetKind } from "@nebulize/sdk";

import { Button, CopyField, EmptyState, Panel, Stack, StatusBadge } from "@/components/ui";

import { useCanvasPairings, useMachines } from "../account/lib/queries";
import { stamp } from "../account/lib/present";
import {
  useBind,
  useDirectoryGrants,
  useInterfaces,
  useInvitations,
  useIssueInvitation,
  useMembers,
  useRebind,
  useUnbind,
} from "../activities/lib/queries";
import { presentedChip, type ReadinessState } from "../editor/lib/readiness";
import { TrayRow } from "../editor/tray/TrayRow";
import { requirementsOf } from "../lib/catalog";
import {
  cardinalityLabel,
  drivenBy,
  fillingLabel,
  interfaceProviders,
  interfaceWiring,
  legalTargetKinds,
  originLabel,
  positionsOf,
  requirementKind,
  servedInstead,
  strayBindings,
  wiringLabel,
  TARGET_KIND_WORDS,
} from "../lib/requirements";
import type { InterfaceWiring, ProviderCandidate } from "../lib/requirements";
import { useSpaceActivities } from "../spaces/lib/queries";
import type { SpaceActivities } from "../spaces/lib/queries";
import type {
  AddedActivityRow,
  CatalogEntry,
  ConsoleBinding,
  NamedRequirement,
} from "../types";

import { Chip, ReqKindChip } from "./Chips";
import { Paged } from "./Paged";
import { Dot, Mono, Muted } from "./Text";

/** One thing a position may be bound to: the id the api takes, and how a person names it. */
interface Target {
  id: string;
  label: string;
  from: string;
  kind: BindingTargetKind;
  detail: string;
}

function matchTarget(target: Target, needle: string): boolean {
  return `${target.label} ${target.from} ${target.id}`.toLowerCase().includes(needle);
}

/** Every real target of each kind this requirement admits, drawn from its own endpoint. */
function useTargets(
  spaceId: string,
  kinds: readonly BindingTargetKind[],
  providers: readonly ProviderCandidate[],
): Target[] {
  const machines = useMachines();
  const pairings = useCanvasPairings();
  const members = useMembers(spaceId);
  const grants = useDirectoryGrants(spaceId);

  const out: Target[] = [];
  if (kinds.includes("machine")) {
    for (const one of machines.data ?? []) {
      out.push({
        id: one.machine_id,
        label: one.label,
        from: TARGET_KIND_WORDS.machine,
        kind: "machine",
        detail: one.short_fingerprint,
      });
    }
  }
  if (kinds.includes("device")) {
    for (const one of pairings.data ?? []) {
      out.push({
        id: one.id,
        label: one.label === "" ? one.canvas_kind : one.label,
        from: TARGET_KIND_WORDS.device,
        kind: "device",
        detail: one.canvas_kind,
      });
    }
  }
  if (kinds.includes("person")) {
    for (const one of members.data ?? []) {
      out.push({
        id: one.account_id,
        label: one.account_id,
        from: TARGET_KIND_WORDS.person,
        kind: "person",
        detail: one.role ?? "member",
      });
    }
  }
  if (kinds.includes("activity")) {
    for (const one of providers) {
      out.push({
        id: one.added_activity_id,
        label: one.name,
        from: TARGET_KIND_WORDS.activity,
        kind: "activity",
        detail: one.running ? "running" : "not running",
      });
    }
  }
  if (kinds.includes("directory")) {
    for (const one of grants.data ?? []) {
      out.push({
        id: one.grant_id,
        label: one.grant_id,
        from: TARGET_KIND_WORDS.directory,
        kind: "directory",
        detail: stamp(one.granted_at),
      });
    }
  }
  return out;
}

function TargetList({
  targets,
  chosen,
  onPick,
}: {
  readonly targets: readonly Target[];
  readonly chosen: string | null;
  readonly onPick: (target: Target) => void;
}) {
  if (targets.length === 0) {
    return <EmptyState bare headline="Nothing of this kind" />;
  }

  return (
    <Paged rows={targets} pageSize={6} placeholder="Find one" match={matchTarget}>
      {(page) => (
        <Stack gap={1}>
          {page.map((target) => (
            <button
              type="button"
              key={target.id}
              className={chosen === target.id ? "cx-candidate cx-candidate-on" : "cx-candidate"}
              onClick={() => {
                onPick(target);
              }}
            >
              <span className="cx-candidate-name">{target.label}</span>
              <span className="cx-candidate-from">{target.from}</span>
              <span className="cx-candidate-detail">{target.detail}</span>
              <span className="cx-candidate-state">{target.kind}</span>
            </button>
          ))}
        </Stack>
      )}
    </Paged>
  );
}

/** The interface states that count as filled: something is bound and it is still there. */
const FILLED = new Set<InterfaceWiring["state"]>(["running", "not_running", "unreadable"]);

const DOTS: Record<InterfaceWiring["state"], "live" | "warn" | "wait" | "off"> = {
  unwired: "wait",
  provider_removed: "wait",
  running: "live",
  not_running: "warn",
  unreadable: "warn",
};

function PositionRow({
  added,
  spaceId,
  requirement,
  position,
  binding,
  targets,
  wiring,
}: {
  readonly added: AddedActivityRow;
  readonly spaceId: string;
  readonly requirement: NamedRequirement;
  readonly position: number;
  readonly binding: ConsoleBinding | null;
  readonly targets: readonly Target[];
  /** Set for an interface position, whose reading is the activity it names, not a label. */
  readonly wiring: InterfaceWiring | null;
}) {
  const [open, setOpen] = useState(false);
  const bind = useBind(added.added_activity_id);
  const rebind = useRebind();
  const unbind = useUnbind();
  const invite = useIssueInvitation(spaceId);
  const invitations = useInvitations(spaceId);

  const pending = (invitations.data ?? []).some(
    (one) =>
      one.added_activity_id === added.added_activity_id &&
      one.requirement_name === requirement.name &&
      one.position === position &&
      one.status !== "claimed",
  );
  const held = targets.find((one) => one.id === binding?.target_label);
  const filled = wiring === null ? binding !== null : FILLED.has(wiring.state);
  const failure = bind.error ?? rebind.error;

  return (
    <TrayRow
      dim={!filled}
      title={
        <span className="cx-position-title">
          <Dot tone={filled ? DOTS[wiring?.state ?? "running"] : "wait"} />
          {`${requirement.name} ${String(position)}`}
        </span>
      }
      badge={
        wiring === null || binding === null ? undefined : (
          <StatusBadge tone={wiring.automatic ? "info" : "neutral"}>
            {originLabel(binding)}
          </StatusBadge>
        )
      }
      meta={
        <Muted small>
          {wiring !== null
            ? wiringLabel(wiring)
            : binding === null
              ? "nothing bound"
              : `${held?.label ?? binding.target_label} (${binding.target_kind ?? "activity"})`}
        </Muted>
      }
      submeta={wiring === null || wiring.remedy === "" ? undefined : <Muted small>{wiring.remedy}</Muted>}
      actions={
        <>
          {invite.data !== undefined && invite.variables?.position === position ? (
            <CopyField value={invite.data.code} label="invitation code" />
          ) : null}
          {binding === null && pending ? <StatusBadge tone="info">invited</StatusBadge> : null}
          {binding === null && targets.some((one) => one.kind === "person") ? (
            <Button
              size="sm"
              variant="ghost"
              loading={invite.isPending}
              onClick={() => {
                invite.mutate({
                  added_activity_id: added.added_activity_id,
                  requirement_name: requirement.name,
                  position,
                });
              }}
            >
              Invite
            </Button>
          ) : null}
          {targets.length === 0 && binding === null ? null : (
            <Button
              size="sm"
              variant={filled ? "ghost" : "secondary"}
              onClick={() => {
                setOpen((was) => !was);
              }}
            >
              {open ? "Done" : filled ? "Change" : "Choose"}
            </Button>
          )}
          {binding === null ? null : (
            <Button
              size="sm"
              variant="ghost"
              loading={unbind.isPending}
              onClick={() => {
                unbind.mutate(binding.binding_id);
              }}
            >
              Unbind
            </Button>
          )}
        </>
      }
      detail={
        open ? (
          <>
            <TargetList
              targets={targets}
              chosen={binding?.target_label ?? null}
              onPick={(target) => {
                const done = {
                  onSuccess: () => {
                    setOpen(false);
                  },
                };
                if (binding === null) {
                  bind.mutate(
                    {
                      requirement_name: requirement.name,
                      position,
                      target_kind: target.kind,
                      target_id: target.id,
                    },
                    done,
                  );
                } else {
                  rebind.mutate(
                    {
                      binding_id: binding.binding_id,
                      target_kind: target.kind,
                      target_id: target.id,
                    },
                    done,
                  );
                }
              }}
            />
            {failure === null ? null : <StatusBadge tone="danger">{failure.message}</StatusBadge>}
          </>
        ) : undefined
      }
    />
  );
}

/** One requirement's `ReadinessState`, from its positions' own wiring or binding presence. */
function requirementState(
  added: AddedActivityRow,
  requirement: NamedRequirement,
  wired: boolean,
  providers: readonly ProviderCandidate[],
  activities: SpaceActivities,
): ReadinessState {
  const positions = positionsOf(added, requirement);
  const available = positions.length;
  const optional = requirement.required === false;

  if (wired) {
    const filled = positions.filter((row) => {
      const reading = interfaceWiring(row.binding, activities.rows, providers, activities.liveRead);
      return reading.state !== "unwired" && reading.state !== "provider_removed";
    }).length;
    if (filled >= available && available > 0) return "ready";
    if (providers.length === 0) return optional ? "optional_unwired" : "unavailable";
    if (providers.length > 1) return optional ? "optional_unwired" : "ambiguous";
    return optional ? "optional_unwired" : "unwired";
  }

  const filled = positions.filter((row) => row.binding !== null).length;
  if (filled >= available && available > 0) return "ready";
  return optional ? "optional_unwired" : "unwired";
}

function RequirementBlock({
  added,
  spaceId,
  requirement,
  activities,
}: {
  readonly added: AddedActivityRow;
  readonly spaceId: string;
  readonly requirement: NamedRequirement;
  readonly activities: SpaceActivities;
}) {
  const kinds = legalTargetKinds(requirement.type);
  const from = drivenBy(requirement.count);
  const wired = requirementKind(requirement.type) === "interface" && kinds.includes("activity");
  const providers: ProviderCandidate[] = wired
    ? interfaceProviders(
        requirement.type,
        activities.rows,
        activities.entryFor,
        added.added_activity_id,
      )
    : [];
  const targets = useTargets(spaceId, kinds, providers);
  const unbind = useUnbind();
  const registry = useInterfaces();

  const served = servedInstead(requirement.type);
  const summary = (registry.data ?? []).find((one) => one.name === requirement.type)?.summary ?? "";
  const chip = presentedChip(requirementState(added, requirement, wired, providers, activities));
  const countLabel = `${cardinalityLabel(requirement.count)}${from === null ? "" : `, from ${from}`}`;

  return (
    <div className="cx-reqblock">
      <TrayRow
        title={<b className="cx-node-name">{requirement.name}</b>}
        badge={<StatusBadge tone={chip.tone}>{chip.label}</StatusBadge>}
        meta={
          <div className="cx-chips">
            <Mono>{requirement.type}</Mono>
            <ReqKindChip type={requirement.type} summary={summary} />
            <Chip>{countLabel}</Chip>
            {requirement.required === false ? (
              <Chip tone="ns">optional</Chip>
            ) : (
              <span className="cx-req">required</span>
            )}
            {kinds.length > 0 ? (
              kinds.map((kind) => <Chip key={kind}>{kind}</Chip>)
            ) : (
              <Chip tone="ns">{served ?? ""}</Chip>
            )}
          </div>
        }
      />

      {!wired || providers.length > 0 ? null : (
        <div className="cx-candidates">
          <EmptyState bare headline="Nothing added to this space provides it" />
        </div>
      )}

      {positionsOf(added, requirement).map((row) => (
        <PositionRow
          key={row.position}
          added={added}
          spaceId={spaceId}
          requirement={requirement}
          position={row.position}
          binding={row.binding}
          targets={targets}
          wiring={
            wired
              ? interfaceWiring(row.binding, activities.rows, providers, activities.liveRead)
              : null
          }
        />
      ))}

      {strayBindings(added, requirement).map((binding) => (
        <TrayRow
          key={binding.binding_id}
          title={
            <span className="cx-position-title">
              <Dot tone="warn" />
              {`${requirement.name} ${String(binding.position)}`}
            </span>
          }
          badge={<StatusBadge tone="warning">out of range</StatusBadge>}
          meta={<Muted small>{binding.target_label}</Muted>}
          actions={
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                unbind.mutate(binding.binding_id);
              }}
            >
              Unbind
            </Button>
          }
        />
      ))}
    </div>
  );
}

/** Every requirement of one added activity, one row per position, each with its real targets. */
export function Requirements({
  added,
  entry,
  spaceId,
  framed,
}: {
  readonly added: AddedActivityRow;
  readonly entry: CatalogEntry;
  readonly spaceId: string;
  readonly framed?: boolean;
}) {
  const requirements = requirementsOf(entry);
  const activities = useSpaceActivities(spaceId);

  const body =
    requirements.length === 0 ? (
      <EmptyState bare headline="Requires nothing" />
    ) : (
      <Stack gap={5}>
        {requirements.map((requirement) => (
          <RequirementBlock
            key={requirement.name}
            added={added}
            spaceId={spaceId}
            requirement={requirement}
            activities={activities}
          />
        ))}
      </Stack>
    );

  if (framed !== true) {
    return (
      <div className="cx-brings">
        <span className="cx-key">Requires</span>
        <div style={{ flex: 1, minWidth: "260px" }}>{body}</div>
      </div>
    );
  }

  return <Panel title="Requires">{body}</Panel>;
}

/** A requirement list before anything is added: what a customer would have to fill, unbound. */
export function RequirementsPreview({
  entry,
  framed,
}: {
  readonly entry: CatalogEntry;
  readonly framed?: boolean;
}) {
  const requirements = requirementsOf(entry);
  const registry = useInterfaces();

  const body =
    requirements.length === 0 ? (
      <EmptyState bare headline="Requires nothing" />
    ) : (
      <Stack gap={3}>
        {requirements.map((requirement) => {
          const summary =
            (registry.data ?? []).find((one) => one.name === requirement.type)?.summary ?? "";
          const from = drivenBy(requirement.count);
          const countLabel = `${cardinalityLabel(requirement.count)}${from === null ? "" : `, from ${from}`}`;
          return (
            <div className="cx-reqblock" key={requirement.name}>
              <TrayRow
                title={<b className="cx-node-name">{requirement.name}</b>}
                meta={
                  <div className="cx-chips">
                    <Mono>{requirement.type}</Mono>
                    <ReqKindChip type={requirement.type} summary={summary} />
                    <Chip>{countLabel}</Chip>
                    {requirement.required === false ? (
                      <Chip tone="ns">optional</Chip>
                    ) : (
                      <span className="cx-req">required</span>
                    )}
                    <Chip>{fillingLabel(requirement.type)}</Chip>
                  </div>
                }
              />
            </div>
          );
        })}
      </Stack>
    );

  if (framed !== true) {
    return (
      <div className="cx-brings">
        <span className="cx-key">Requires</span>
        <div style={{ flex: 1, minWidth: "260px" }}>{body}</div>
      </div>
    );
  }

  return <Panel title="Requires">{body}</Panel>;
}
